import type { IdentityCallbackData } from 'branch-sdk';

import { config } from 'src/config';

export interface IdentifyParameters {
  email?: string;
  userId?: number;
  intercomSha?: string;
}

export interface IdentifyOptions {
  skipSegment?: boolean;
  skipBranch?: boolean;
  skipHotjar?: boolean;
}

export interface SegmentIdentifyTraits {
  account_type?: 'pro' | 'homeowner' | 'insurance';
  platform_type?: 'WEB';
}

export interface SegmentIdentifyParamaters
  extends IdentifyParameters,
    SegmentIdentifyTraits {}

export const TIMEOUT_MILLISECONDS = 3 * 1000;
export const TIMEOUT_MESSAGE = 'telemetry identify timeout';

const createTimeout = () =>
  new Promise<void>(resolve => {
    setTimeout(() => resolve(), TIMEOUT_MILLISECONDS);
  });

const segmentIdentify = (
  params: SegmentIdentifyTraits | SegmentIdentifyParamaters,
): Promise<void | null> => {
  if (!config.SEGMENT_API_KEY) {
    return Promise.resolve();
  }

  return Promise.race([
    new Promise<void>(resolve => {
      if ('userId' in params && params.userId) {
        const { userId, intercomSha, ...traits } = params;

        window.analytics.identify(
          userId.toString(),
          traits,
          {
            integrations: { Intercom: { user_hash: intercomSha } },
          },
          resolve,
        );
      } else {
        window.analytics.identify(params, resolve);
      }
    }),
    createTimeout(),
  ]);
};

const branchIdentify = ({
  email = '',
}: IdentifyParameters): Promise<IdentityCallbackData | null> => {
  if (!config.BRANCH_API_KEY || !window.branch) {
    return Promise.resolve(null);
  }

  return new Promise((resolve, reject) => {
    try {
      window.branch.setIdentity(email, (err, data) =>
        err ? reject(err) : resolve(data),
      );
    } catch (e) {
      reject(e);
    }
  });
};

const hotjarIdentify = ({ userId }: IdentifyParameters): Promise<void> =>
  new Promise((resolve, reject) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if ((window as any).hj) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        (window as any).hj('identify', userId, { user_id: userId });
      }
      resolve();
    } catch (e) {
      reject(e);
    }
  });

const identify = async (
  parameters: IdentifyParameters,
  options?: IdentifyOptions,
): Promise<IdentityCallbackData | null> => {
  const { skipBranch, skipSegment, skipHotjar } = options ?? {};
  const identifyAll = Promise.all([
    skipSegment ? Promise.resolve() : segmentIdentify(parameters),
    skipBranch ? Promise.resolve() : branchIdentify(parameters),
    skipHotjar ? Promise.resolve() : hotjarIdentify(parameters),
  ]);

  await Promise.race([identifyAll, createTimeout()]);

  return null;
};

export { identify, segmentIdentify };
